<template>
  <div class="anchor-float">
    <!-- 折叠态：小地图（独立态可拖拽；嵌入总控时由总控面板承载，自身不再浮动） -->
    <div
      class="af-mini"
      :class="{ embedded }"
      ref="miniEl"
      v-if="!open"
      :style="miniStyle"
      @pointerdown="onMiniDown"
      @click="onMiniClick"
    >
      <div class="af-mini-head">
        <span class="af-room" :title="roomName || room || '未进房'">{{ roomLabel }}</span>
        <span class="af-count">{{ count }} 锚</span>
        <!-- 状态计数：紧凑小 chip 放到标题行右侧，省掉一整行；首格是房间当前 CD 倒计时（有才显示） -->
        <div class="af-bar" v-if="points.length">
          <span v-if="currentCdLive > 0" class="st cd" title="当前房间CD">CD {{ currentCdLive }}s</span>
          <span class="st available" title="可用">{{ counts.available }}</span>
          <span class="st cooling" title="冷却中">{{ counts.cooling }}</span>
          <span class="st reusable" title="可复用">{{ counts.reusable }}</span>
          <span class="st used" title="已使用">{{ counts.used }}</span>
        </div>
      </div>

      <template v-if="points.length">
        <!-- 焦点 + 最新 合并为一行：标签内联在色点左侧，充分利用横向空间 -->
        <div class="af-dots-line">
          <span class="af-ico ico-focus" title="焦点：当前跳转点前后各 3 个">◎</span>
          <div class="af-dots af-dots-focus">
            <span
              v-for="p in focusPoints"
              :key="'f' + p.index"
              class="af-dot"
              :class="[dotWinClass(p), 'is-' + resState(p), { 'is-current': p.isCurrent }]"
              :title="dotTitle(p)"
            ><span v-if="p.roundUseCount > 0 && dotCdNum(p) === 0" class="af-dot-n">×{{ p.roundUseCount }}</span><span v-if="dotCdNum(p) > 0" class="af-dot-cd">{{ dotCdNum(p) }}</span></span>
          </div>
          <template v-if="latestPoints.length">
            <span class="af-sep"></span>
            <span class="af-ico ico-latest" title="最新：末尾最近几个">✦</span>
            <div class="af-dots">
              <span
                v-for="p in latestPoints"
                :key="'l' + p.index"
                class="af-dot"
                :class="[dotWinClass(p), 'is-' + resState(p), { 'is-current': p.isCurrent }]"
                :title="dotTitle(p)"
                ><span v-if="p.roundUseCount > 0 && dotCdNum(p) === 0" class="af-dot-n">×{{ p.roundUseCount }}</span><span v-if="dotCdNum(p) > 0" class="af-dot-cd">{{ dotCdNum(p) }}</span></span>
            </div>
          </template>
        </div>
      </template>
      <div class="af-dots-empty" v-else>无锚点</div>
    </div>

    <!-- 展开态：完整锚点面板（点开出现，不做跳页）。
         ★ 必须 Teleport 到 body：面板是 fixed 定位，而总控窗（父级）上有
         backdrop-filter —— 它会成为 fixed 的包含块，把 960px 的大面板
         收窄进 326px 的悬浮窗里。传到 body 后固定定位才相对视口生效。 -->
    <Teleport to="body">
      <!-- 包装 div 与组件根同名：面板样式是按 .anchor-float .af-modal 嵌套写的，
           Teleport 后 modal 不在原根节点内，包一层让选择器继续命中（本身无样式） -->
      <div class="anchor-float">
        <div class="af-modal" v-if="open">
          <div class="af-mask" @click="toggle"></div>
          <div class="af-panel">
        <!-- 头部 -->
        <div class="af-head">
          <div class="af-title">
            锚点状态
            <span class="af-room2">{{ roomName || room || '未进房' }}</span>
          </div>
          <div class="af-meta">
            <span>复用{{ reuseEnabled ? '开' : '关' }}</span>
            <span>冷却 {{ cooldownSeconds }}s</span>
            <span v-if="currentCd != null">当前CD {{ formatCd(currentCd) }}</span>
            <span>共 {{ count }}</span>
          </div>
          <span class="af-close" @click="toggle">✕</span>
        </div>

        <!-- 图例 -->
        <div class="af-legend">
          <span class="lg"><i class="sw w-Zhuang"></i>庄</span>
          <span class="lg"><i class="sw w-Xian"></i>闲</span>
          <span class="lg"><i class="sw w-He"></i>和</span>
          <span class="lg-sep"></span>
          <span class="lg"><i class="sw is-available"></i>可用</span>
          <span class="lg"><i class="sw is-cooling"></i>冷却</span>
          <span class="lg"><i class="sw is-reusable"></i>可复用</span>
          <span class="lg"><i class="sw is-used"></i>已用</span>
          <span class="lg"><i class="sw is-current"></i>当前</span>
        </div>

        <!-- 卡片网格（复刻流程追踪的锚点视图） -->
        <div class="af-grid" v-if="points.length">
          <div
            v-for="p in points"
            :key="p.index"
            class="acard"
            :class="cardClass(p)"
            :title="cardTitle(p)"
          >
            <span v-if="p.isCurrent" class="a-cur">当前</span>
            <div class="a-rs">{{ p.ju }}-{{ p.rs }}</div>
            <div class="a-dl">
              <span class="a-dl-name">{{ p.dealer || '-' }}</span>
              <span v-if="p.roundUseCount > 0" class="a-cnt">×{{ p.roundUseCount }}</span>
            </div>
            <div class="a-win">{{ winText(p) }}</div>
            <div class="a-cd" :class="{ live: showCd(p) }" :data-cd="cdValue(p)">{{ cdLabel(p) }}</div>
          </div>
        </div>
        <div class="af-empty" v-else>该房间暂无锚点（未开启杀猪或无数据时为空）</div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import Config from '@/ts/Config';

const props = defineProps<{ room: string; roomName?: string; embedded?: boolean }>();
const room = ref(props.room);

// 跟随玩家所在房间：room 变化立即重连推送（切房间 = 换 room 参数重连，服务端即刻推一帧）
// 注意：首连放在 onMounted，否则 immediate 会在 setup 阶段同步调用 connect()，
// 那时下面的 let/const（es/data）还没初始化，会抛 "Cannot access 'es' before initialization"
watch(
  () => props.room,
  (v) => { room.value = v || ''; connect(); }
);

const STATE_LABEL: Record<string, string> = {
  available: '可用', cooling: '冷却中', reusable: '可复用', used: '已使用', pending: '待结算', raw: '原始'
};
const WIN_PRIMARY: Record<string, string> = { Zhuang: '庄', Xian: '闲', He: '和' };
const WIN_EXTRA: Record<string, string> = {
  ZhuangDui: '庄对', XianDui: '闲对', Da: '大', Xiao: '小', RenYiDui: '任意对', WanMeiDui: '完美对'
};

function winParts(raw: string) {
  let key = 'None', primary = '', text = '未结算';
  if (raw && raw !== 'None') {
    const extras: string[] = [];
    for (const n of raw.split(',').map((s) => s.trim()).filter(Boolean)) {
      if (WIN_PRIMARY[n]) { key = n; primary = WIN_PRIMARY[n]; }
      else if (WIN_EXTRA[n]) extras.push(WIN_EXTRA[n]);
    }
    text = (primary || '未结算') + (extras.length ? '+' + extras.join('+') : '');
  }
  return { key, primary, text };
}

// ===== 数据拉取（走 SSE 推送，和流程追踪同一条 auxStream；后端只在状态变化时推一帧）=====
const data = ref<any>(null);
const open = ref(false);
const fetchedAt = ref(0);
const clock = ref(0); // 每秒自增，驱动 CD 本地倒计时重渲染

const points = computed(() => data.value?.points || []);
const count = computed(() => data.value?.count ?? 0);
const reuseEnabled = computed(() => !!data.value?.reuseEnabled);
const cooldownSeconds = computed(() => data.value?.cooldownSeconds ?? 0);
const currentCd = computed(() => data.value?.currentCd ?? null);
const roomLabel = computed(() => props.roomName || props.room || '未进房');

// 小地图「焦点双段式」：焦点段 = 当前位置 ±3；最新段 = 末尾最近几个
const currentIndex = computed(() => points.value.findIndex((p: any) => p.isCurrent));
const hasCurrent = computed(() => currentIndex.value >= 0);

const focusPoints = computed(() => {
  const arr = points.value;
  if (!arr.length) return [];
  let start: number, end: number;
  if (hasCurrent.value) {
    start = Math.max(0, currentIndex.value - 3);
    end = Math.min(arr.length, currentIndex.value + 4); // 含 +3 ⇒ 末尾 index+3，slice 末位 index+4
  } else {
    start = Math.max(0, arr.length - 7);
    end = arr.length;
  }
  return arr.slice(start, end);
});

const focusIndices = computed(() => new Set(focusPoints.value.map((p: any) => p.index)));

// 最新段：末尾 5 个中剔除已出现在焦点段的，避免和焦点段重复
const latestPoints = computed(() => {
  const arr = points.value;
  return arr
    .slice(Math.max(0, arr.length - 5))
    .filter((p: any) => !focusIndices.value.has(p.index));
});

const counts = computed(() => {
  const c: Record<string, number> = { available: 0, cooling: 0, reusable: 0, used: 0, pending: 0, raw: 0 };
  for (const p of points.value) c[resState(p)] = (c[resState(p)] || 0) + 1;
  return c;
});

// ---- SSE 连接：跟随房间，切房间 = 重连（URL 带 room，连上即推该房间状态）----
let es: EventSource | null = null;
let esFails = 0;
let fallbackTimer: number | null = null;
// 重连定时器必须持有引用：否则组件卸载后它仍会触发 connect()，
// 建出一条没人关闭的 SSE（服务端并发名额只增不减，32 次后所有监控页面 503）。
let retryTimer: number | null = null;
// 连接代号：每次 connect() 递增。旧连接排队中的 aux/onerror 回调一律作废，
// 否则旧连接的 onerror 会把刚建好的新连接 disconnect() 掉（切房后反复闪断）。
let esGen = 0;
// 组件已卸载标记：卸载后禁止任何回调再建连接。
let disposed = false;

function clearRetry() {
  if (retryTimer != null) { clearTimeout(retryTimer); retryTimer = null; }
}

function connect() {
  if (disposed) return;
  clearRetry();                 // 同一时刻只允许存在一个待触发的重连（防定时器堆积/重连风暴）
  const gen = ++esGen;          // 让上一条连接的在途回调失效
  disconnect();
  if (!room.value) { data.value = null; return; }
  // scope=anchors：只要锚点。服务端据此跳过 trace 全量快照与 5 个聚合，
  // 且同一房间的所有遥控器复用同一帧（内容完全一致）。
  const url = `${Config.ORIGIN}/trace/api/auxStream?room=${encodeURIComponent(room.value)}&scope=anchors`;

  let source: EventSource;
  try {
    source = new EventSource(url);
  } catch {
    es = null;
    scheduleFallback();
    return;
  }
  es = source;

  source.addEventListener('aux', (ev: MessageEvent) => {
    if (gen !== esGen) return;  // 旧连接迟到的帧：丢弃
    esFails = 0;
    let d: any;
    try { d = JSON.parse(ev.data); } catch { return; }
    if (d.error) return;
    // aux 帧里带着 anchors；拿到就渲染（服务端只在内容变化时推，缺字段保持原样）
    if (d.anchors) { data.value = d.anchors; fetchedAt.value = Date.now(); }
  });

  let errored = false;
  source.onerror = () => {
    // 旧连接的错误直接忽略：它不能去关掉当前这条（可能是切房后新建的）连接
    if (gen !== esGen || errored) return;
    errored = true;
    // 断线（服务未起 / 旧版本无接口 / 进程重启）：自动重连 + 带鉴权 fetch 兜底
    disconnect();
    esFails++;
    scheduleFallback();

    const delay = Math.min(1000 * esFails, 5000);
    clearRetry();
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      if (disposed || gen !== esGen) return;   // 已卸载 / 已被新连接取代
      if (!es && room.value) connect();
    }, delay);
  };
}

function disconnect() {
  if (es) { es.close(); es = null; }
}

// 推送连不上时，最多每 10s 用带鉴权的 fetch 拉一次兜底；推送一旦恢复立即停用
function scheduleFallback() {
  if (disposed || fallbackTimer != null) return;
  fallbackTimer = window.setTimeout(() => {
    fallbackTimer = null;
    if (disposed || es) return;
    pollOnce();
    scheduleFallback();
  }, 10000);
}

async function pollOnce() {
  if (disposed || !room.value) return;
  try {
    const url = `${Config.ORIGIN}/trace/api/anchors?room=${encodeURIComponent(room.value)}`;
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {};
    if (token) headers['X-Auth-Token'] = token;
    const r = await fetch(url, { headers });
    if (!r.ok) return;
    data.value = await r.json();
    fetchedAt.value = Date.now();
  } catch {
    /* 静默 */
  }
}

const cdTimer = window.setInterval(() => { clock.value++; }, 1000);

// 首连：放到 onMounted，确保所有 let/const 已初始化（避免 TDZ）
onMounted(() => { connect(); });

function toggle() {
  open.value = !open.value;
}

// ===== 小地图拖拽（pointer 事件，桌面/触屏通用；拖动时不触发展开）=====
const miniEl = ref<HTMLElement | null>(null);
const miniPos = ref<{ left: number; top: number } | null>(null); // 拖过后用显式 left/top 固定
const miniStyle = computed(() =>
  miniPos.value
    ? { left: miniPos.value.left + 'px', top: miniPos.value.top + 'px' }
    : {}
);

let dragging = false;
let moved = false;
let startX = 0;
let startY = 0;
let offX = 0;
let offY = 0;

function onMiniDown(e: PointerEvent) {
  if (props.embedded) return; // 嵌入总控面板时，拖拽交给总控面板处理，自身不抢拖拽
  if (!miniEl.value) return;
  dragging = true;
  moved = false;
  startX = e.clientX;
  startY = e.clientY;
  const rect = miniEl.value.getBoundingClientRect();
  if (!miniPos.value) miniPos.value = { left: rect.left, top: rect.top };
  offX = e.clientX - rect.left;
  offY = e.clientY - rect.top;
  window.addEventListener('pointermove', onMiniMove);
  window.addEventListener('pointerup', onMiniUp);
}

function onMiniMove(e: PointerEvent) {
  if (!dragging) return;
  if (Math.abs(e.clientX - startX) > 3 || Math.abs(e.clientY - startY) > 3) moved = true;
  let left = e.clientX - offX;
  let top = e.clientY - offY;
  // 限制在视口内，避免拖出屏幕
  left = Math.max(0, Math.min(left, window.innerWidth - 60));
  top = Math.max(0, Math.min(top, window.innerHeight - 40));
  miniPos.value = { left, top };
}

function onMiniUp() {
  dragging = false;
  window.removeEventListener('pointermove', onMiniMove);
  window.removeEventListener('pointerup', onMiniUp);
}

// 点击：仅当没有拖动过才展开/收起
function onMiniClick() {
  if (moved) { moved = false; return; }
  open.value = !open.value;
}

// ===== 渲染辅助 =====
function dotWinClass(p: any) { return 'w-' + winParts(p.winAreas).key; }
function cardClass(p: any) {
  const w = winParts(p.winAreas);
  const cls = ['acard', 'w-' + w.key, 'is-' + resState(p)];
  if (p.isCurrent) cls.push('is-current');
  return cls;
}
function winText(p: any) { return winParts(p.winAreas).text; }
// 复用关闭时「用过就是废了」：不必等自身 CD 走完才置灰，直接按「已使用」渲染。
// （服务端 state 在 CD 内会给 cooling，那是给"允许复用"口径用的；复用关闭就没"复活"这回事）
function isDead(p: any) { return !reuseEnabled.value && !!p.isUsed; }
function resState(p: any) { return isDead(p) ? 'used' : p.state; }
function showCd(p: any) {
  void clock.value; // 建立依赖，使 CD 每秒重算
  return !isDead(p) && p.selfRemainingCd != null;
}
function liveCd(p: any) {
  if (p.selfRemainingCd == null) return 0;
  const dt = (Date.now() - fetchedAt.value) / 1000;
  return Math.max(0, p.selfRemainingCd - dt);
}
function cdValue(p: any) { return p.selfRemainingCd != null ? liveCd(p) : ''; }
// 小地图头部用的「房间当前 CD」实时倒计时（与大面板头部 currentCd 一致）
const currentCdLive = computed(() => {
  void clock.value;
  if (currentCd.value == null) return 0;
  const dt = (Date.now() - fetchedAt.value) / 1000;
  return Math.max(0, Math.round(currentCd.value - dt));
});
// 小地图色豆上叠加的「自身剩余 CD」秒数（复用关闭=已废弃则不显示）
function dotCdNum(p: any) {
  void clock.value;
  if (p.selfRemainingCd == null || isDead(p)) return 0;
  const v = liveCd(p);
  return v > 0 ? Math.ceil(v) : 0;
}
function cdLabel(p: any) {
  void clock.value;
  if (!isDead(p) && p.selfRemainingCd != null) {
    const v = liveCd(p);
    return v > 0 ? 'CD ' + Math.ceil(v) + 's' : (STATE_LABEL[resState(p)] || resState(p));
  }
  return STATE_LABEL[resState(p)] || resState(p);
}
function formatCd(v: number) {
  return v > 0 ? Math.ceil(v) + 's' : '—';
}

function dotTitle(p: any) {
  return `${p.ju}-${p.rs} ${winText(p)} · ${STATE_LABEL[resState(p)] || resState(p)}` +
    (p.isCurrent ? ' · 当前播放位置' : '') +
    (isDead(p) ? ' · 已废弃(复用关闭)' : '') +
    (p.isHe ? ' · 和局(不参与复用)' : '');
}
function cardTitle(p: any) {
  return `局 ${p.ju}-${p.rs}\n荷官 ${p.dealer}\n输赢 ${winText(p)}\n` +
    `判定 ${STATE_LABEL[resState(p)] || resState(p)}（按整局聚合）\n` +
    (isDead(p) ? '※ 复用已关闭：用过即废弃，不再等冷却\n' : '') +
    (p.isHe ? '※ 和局不参与复用：用过即作废（可用一次）\n' : '') +
    `自身已用=${p.isUsed} · 本条复用 ${p.useCount} 次 · 整局复用 ${p.roundUseCount} 次\n` +
    (p.isCurrent ? '★ 当前播放位置（杀猪跳转后即目标局）\n' : '') +
    `自身CD ${p.selfRemainingCd != null ? '剩余 ' + p.selfRemainingCd + 's' : (p.selfUsedAgoSeconds != null ? '已结束（' + p.selfUsedAgoSeconds + 's前用过）' : '未用过')}`;
}

onUnmounted(() => {
  disposed = true;
  esGen++;            // 让所有在途回调（aux / onerror / 重连）失效
  clearRetry();       // 关键：不清的话定时器会在卸载后触发 connect()，建出一条永远没人关闭的 SSE
  disconnect();
  if (fallbackTimer != null) { clearTimeout(fallbackTimer); fallbackTimer = null; }
  clearInterval(cdTimer);
});
</script>

<style scoped lang="scss">
.anchor-float {
  // 折叠小地图：固定在详情卡左下角，始终悬浮可见
  .af-mini {
    position: fixed;
    left: 16px;
    bottom: 16px;
    z-index: 20;
    width: 168px;
    border-radius: 12px;
    padding: 8px 10px 9px;
    cursor: grab;
    user-select: none;
    touch-action: none;
    background: linear-gradient(165deg, rgba(15, 23, 42, 0.96), rgba(2, 6, 23, 0.96));
    // 壳用冷蓝黑（主界面是暖金黑），色温对比 + 亮边框 + 重投影，浮在主界面上不再「保护色」
    border: 1px solid rgba(56, 189, 248, 0.38);
    box-shadow: 0 14px 34px -10px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(56, 189, 248, 0.10),
      inset 0 1px 0 rgba(148, 197, 253, 0.10);
    backdrop-filter: blur(10px) saturate(1.2);
    transition: border-color 0.15s, box-shadow 0.15s;

    &:hover {
      border-color: rgba(125, 211, 252, 0.65);
      box-shadow: 0 16px 38px -10px rgba(0, 0, 0, 0.92), 0 0 0 1px rgba(56, 189, 248, 0.18),
        inset 0 1px 0 rgba(148, 197, 253, 0.14);
    }
    &:active { cursor: grabbing; }
  }

  // 嵌入总控面板时：取消自身浮动/边框/背景，作为面板内的一段内容（顶部加分隔线）
  .af-mini.embedded {
    position: static;
    left: auto; top: auto; bottom: auto; right: auto;
    width: auto;
    margin-top: 9px;
    padding: 9px 0 0;
    background: none;
    border: none;
    border-top: 1px solid rgba(216, 178, 95, 0.18);
    border-radius: 0;
    box-shadow: none;
    backdrop-filter: none;
    cursor: default;
    transition: none;

    &:hover { border-color: transparent; box-shadow: none; }
    &:active { cursor: default; }
  }

  .af-mini-head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 7px;
  }

  .af-room {
    font-size: 12px;
    font-weight: 700;
    color: var(--kp-gold-hi, #f6e3b6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 110px;
  }

  .af-count {
    font-size: 10px;
    color: var(--kp-faint, #8a7d63);
    flex-shrink: 0;
    margin-left: 6px;
  }

  // 焦点/最新合并行的组图标与分隔线（文字标签太占地方，用符号代替）
  .af-ico {
    flex: none;
    font-size: 11px;
    line-height: 1;

    &.ico-focus  { color: #38bdf8; text-shadow: 0 0 5px #38bdf899; }
    &.ico-latest { color: #d8b25f; }
  }

  .af-sep {
    width: 1px;
    height: 12px;
    background: rgba(216, 178, 95, 0.25);
    flex: none;
  }

  // 焦点 + 最新 合并行：一行放下，放不下自动换行
  .af-dots-line {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 6px;
    margin-top: 7px;
  }

  .af-dots {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
  }

  .af-dots-empty {
    font-size: 10px;
    color: var(--kp-faint, #8a7d63);
    padding: 4px 0 6px;
  }

  .af-dot {
    position: relative;
    width: 15px;
    height: 15px;
    border-radius: 3px;
    background: var(--win, #334155);
    border: 1.5px solid var(--edge, #334155);
    opacity: var(--dimc, 1);
    filter: var(--dimf, none);

    &.w-Zhuang { --win: #ef4444; }
    &.w-Xian   { --win: #3b82f6; }
    &.w-He     { --win: #22c55e; }
    &.w-None   { --win: #334155; }
    &.is-available { --edge: #1d6b4a; }
    &.is-cooling   { --edge: #f59e0b; }
    &.is-reusable  { --edge: #0ea5e9; }
    // 已使用：不再灰成一块——保留庄红/闲蓝/和绿底色（降透明度）+ 灰边 + 斜杠线，
    // 小地图上也能看出这一豆原本是什么输赢
    &.is-used {
      --edge: #64748b;
      --dimc: 0.5;
      --dimf: none;

      &::after {
        content: '';
        position: absolute;
        left: 1px;
        right: 1px;
        top: 50%;
        height: 1.5px;
        margin-top: -0.75px;
        border-radius: 1px;
        background: rgba(241, 245, 249, 0.85);
        transform: rotate(-40deg);
      }
    }
    &.is-pending   { --edge: #475569; --dimc: 0.7; }
    &.is-raw       { --edge: #334155; --dimc: 0.55; }
    // 当前点（最新段里的）：实线环 + 发光，和焦点段同款但小一号
    &.is-current   { --edge: #38bdf8; box-shadow: 0 0 0 2px #38bdf8, 0 0 8px #38bdf8aa; }
  }

  // 焦点段的当前点：放大 + 实线环 + 虚线外框 + 呼吸发光 —— 小地图上最醒目的存在
  .af-dots-focus .af-dot.is-current {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    margin: 0 5px;
    box-shadow: 0 0 0 2px #38bdf8, 0 0 10px #38bdf8cc;
    outline: 1.5px dashed #7dd3fc;
    outline-offset: 2.5px;
    animation: af-cur-pulse 1.6s ease-in-out infinite;
  }

  // 呼吸：光环明暗缓慢起伏，扫一眼就能锁定当前播放位置
  @keyframes af-cur-pulse {
    0%, 100% { box-shadow: 0 0 0 2px #38bdf8, 0 0 5px #38bdf899; }
    50%      { box-shadow: 0 0 0 2px #7dd3fc, 0 0 15px #38bdf8ee; }
  }

  // 色豆角标：复用次数（金角标）与自身剩余 CD（深色底居中），都不占额外空间
  .af-dot-n, .af-dot-cd {
    position: absolute;
    font-size: 8px;
    line-height: 1;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    pointer-events: none;
    border-radius: 4px;
    padding: 0 2px;
    z-index: 1;
  }
  .af-dot-n {
    top: 0;
    right: 1px;
    color: #0c0a06;
    background: #f6e3b6;
  }
  .af-dot-cd {
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: #fcd34d;
    background: rgba(8, 6, 4, 0.82);
  }

  // 状态计数：紧凑小 chip，放在标题行右侧（不再独占整行）
  .af-bar {
    display: flex;
    gap: 3px;
    margin-left: auto;

    .st {
      min-width: 24px;
      text-align: center;
      font-size: 10px;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      padding: 1px 4px;
      border-radius: 4px;
      color: #fff;

      &.available { background: rgba(29, 107, 74, 0.6); }
      &.cooling   { background: rgba(245, 158, 11, 0.55); }
      &.reusable  { background: rgba(14, 165, 233, 0.55); }
      &.used {
        background: rgba(71, 85, 105, 0.6);
        position: relative;
        overflow: hidden;

        // 与色豆「已用」一致：加一道斜杠线（斜杠 = 已用），不再只是灰块
        &::after {
          content: '';
          position: absolute;
          left: -1px;
          right: -1px;
          top: 50%;
          height: 1.5px;
          margin-top: -0.75px;
          background: rgba(241, 245, 249, 0.85);
          transform: rotate(-40deg);
        }
      }
      // 房间当前 CD 倒计时：金色数字、深底描边，与状态计数区分
      &.cd {
        background: rgba(8, 6, 4, 0.55);
        color: #fcd34d;
        border: 1px solid rgba(252, 211, 77, 0.4);
        min-width: 0;
      }
    }
  }

  // ===== 展开面板 =====
  .af-modal {
    position: fixed;
    inset: 0;
    // 嵌入总控面板时，面板自身 z-index=2000 会形成层叠上下文，
    // 故这里要高于 2000 才能盖住总控面板；同时脱离面板背景覆盖全屏
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .af-mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(2px);
  }

  .af-panel {
    position: relative;
    width: min(960px, 94vw);
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    border-radius: 14px;
    background: linear-gradient(180deg, #16120b, #100c07);
    border: 1px solid rgba(216, 178, 95, 0.32);
    box-shadow: 0 24px 70px -20px #000, 0 0 40px -20px rgba(240, 208, 138, 0.5);
    overflow: hidden;
  }

  .af-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 16px;
    border-bottom: 1px solid rgba(216, 178, 95, 0.18);
    flex-shrink: 0;

    .af-title {
      font-size: 15px;
      font-weight: 800;
      color: var(--kp-gold-hi, #f6e3b6);
      display: flex;
      align-items: baseline;
      gap: 8px;
    }

    .af-room2 {
      font-size: 12px;
      font-weight: 600;
      color: var(--kp-muted, #b8ab8e);
    }

    .af-meta {
      display: flex;
      gap: 12px;
      font-size: 11px;
      color: var(--kp-faint, #8a7d63);
      font-variant-numeric: tabular-nums;
      margin-left: auto;
    }

    .af-close {
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      font-size: 16px;
      color: var(--kp-gold, #d8b25f);
      cursor: pointer;
      flex-shrink: 0;
      border: 1px solid rgba(216, 178, 95, 0.2);

      &:hover {
        background: rgba(216, 178, 95, 0.12);
        color: #fff6e0;
      }
    }
  }

  .af-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 9px 16px;
    border-bottom: 1px solid rgba(216, 178, 95, 0.14);
    font-size: 10px;
    color: var(--kp-faint, #8a7d63);
    flex-shrink: 0;

    .lg {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .lg-sep {
      width: 1px;
      height: 12px;
      background: rgba(216, 178, 95, 0.2);
    }

    .sw {
      width: 12px;
      height: 12px;
      border-radius: 3px;
      display: inline-block;
      border: 1.5px solid var(--edge, #334155);
      background: var(--win, #334155);

      &.w-Zhuang { --win: #ef4444; }
      &.w-Xian   { --win: #3b82f6; }
      &.w-He     { --win: #22c55e; }
      &.is-available { --edge: #1d6b4a; }
      &.is-cooling   { --edge: #f59e0b; }
      &.is-reusable  { --edge: #0ea5e9; }
      &.is-used      { --edge: #475569; }
      &.is-current   { --edge: #38bdf8; }
    }
  }

  .af-grid {
    flex: 1;
    overflow-y: auto;
    // 等宽列：
    // 1) 用 minmax(0, 1fr) 而不是 1fr —— 1fr 的隐含最小值是 auto，长文本（如“庄+闲对+大+任意对+完美对”）
    //    会把所在列撑宽，导致同一行卡片宽窄不一；minmax(0, 1fr) 强制每列等宽，超出交给 .a-win 的省略号。
    // 2) auto-fill + 160px：960px 宽的面板正好 5 列（宁可少一列，也不把文字挤成省略号）；更窄的窗口自动降列。
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    // 行高按内容收缩、顶对齐：否则 flex:1 的容器在行数少时会 stretch 把每行拉高（手机 2 列时卡片巨高）
    align-content: start;
    gap: 8px;
    padding: 14px 16px;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb {
      background: rgba(216, 178, 95, 0.25);
      border-radius: 3px;
    }
  }

  .af-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--kp-faint, #8a7d63);
    font-size: 13px;
  }

  // 手机模式：面板占满全屏、网格铺满不留右侧空白，整体不再拥挤
  @media (max-width: 600px) {
    .af-modal {
      align-items: stretch;
      justify-content: stretch;
    }
    .af-panel {
      width: 100vw;
      max-height: none;
      height: 100vh;
      height: 100dvh;
      border-radius: 0;
      border: none;
    }
    .af-head { padding: 12px 14px; gap: 10px; }
    .af-legend { padding: 8px 14px; gap: 8px; }
    .af-grid {
      padding: 12px 14px;
      gap: 8px;
      grid-template-columns: repeat(2, minmax(0, 1fr)); // 手机每行 2 个，等宽
    }
  }
}

// ===== 锚点卡片（复刻流程追踪的视觉）=====
.acard {
  border: 1px solid var(--edge, #334155);
  border-left: 3px solid var(--win, #334155);
  border-radius: 7px;
  padding: 4px 6px 5px;
  background: #0f1520;
  position: relative;
  font-size: 11px;
  line-height: 1.4;
  // 网格项默认 min-width:auto，长内容会把卡片撑出轨道；置 0 才能让 .a-win 的省略号真正生效
  min-width: 0;

  &.w-Zhuang { --win: #ef4444; background: linear-gradient(90deg, rgba(239, 68, 68, 0.40), rgba(239, 68, 68, 0.17)); }
  &.w-Xian   { --win: #3b82f6; background: linear-gradient(90deg, rgba(59, 130, 246, 0.40), rgba(59, 130, 246, 0.17)); }
  &.w-He     { --win: #22c55e; background: linear-gradient(90deg, rgba(34, 197, 94, 0.36), rgba(34, 197, 94, 0.15)); }
  &.w-None   { --win: #334155; background: #0f1520; }

  &.is-available { --edge: #1d6b4a; }
  &.is-cooling   { --edge: #f59e0b; }
  &.is-reusable  { --edge: #0ea5e9; }
  &.is-used      { --edge: #475569; --dimc: 0.5; --dimf: grayscale(1); background-image: linear-gradient(#0d1420f2, #0d1420f2); }
  &.is-pending   { --edge: #475569; --dimc: 0.75; --dimf: grayscale(0.6); }
  &.is-raw       { --edge: #334155; --dimc: 0.62; --dimf: grayscale(0.7); }

  &.is-current { --dimc: 1; --dimf: none; box-shadow: 0 0 0 2px #38bdf8, 0 0 10px #38bdf866; }
  &.is-current.is-used { box-shadow: 0 0 0 2px #38bdf8, 0 0 14px #38bdf8dd; }

  .a-rs, .a-dl, .a-win, .a-cd { opacity: var(--dimc, 1); filter: var(--dimf, none); }

  .a-cur {
    position: absolute;
    top: -8px;
    right: -4px;
    background: #38bdf8;
    color: #082f49;
    font-size: 9px;
    font-weight: 700;
    border-radius: 4px;
    padding: 0 4px;
    line-height: 14px;
    box-shadow: 0 1px 3px #0008;
  }

  .a-rs {
    font-weight: 700;
    color: #e8e2d2;
    font-variant-numeric: tabular-nums;
  }

  .a-dl {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    color: #9a9384;

    .a-dl-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .a-cnt {
      font-size: 9px;
      color: #f6e3b6;
      background: rgba(216, 178, 95, 0.18);
      border: 1px solid rgba(216, 178, 95, 0.3);
      border-radius: 4px;
      padding: 0 3px;
      flex-shrink: 0;
    }
  }

  .a-win {
    font-weight: 700;
    color: #f3ecdb;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .a-cd {
    margin-top: 2px;
    font-size: 10px;
    font-weight: 600;
    color: #cdc6b6;
    font-variant-numeric: tabular-nums;

    &.live { color: #fcd34d; }
  }

  // 手机模式：卡片内边距略放宽，避免内容贴边显得挤
  @media (max-width: 600px) {
    padding: 6px 8px 7px;
  }
}
</style>
